import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchBookings, type Booking } from "@/lib/bookings";
import { listRows } from "@/lib/adminApi";
import { COUPON_STATUS, commerceMeta, type Coupon } from "@/lib/adminCommerce";
import type { Review } from "@/lib/adminModules";
import {
  DataTable,
  ErrorBanner,
  LoadingState,
  PageHeader,
  StatCards,
  StatusPill,
  btnGhost,
  btnPrimary,
  type Column,
} from "@/pages/admin/components/AdminUI";
import { formatRupees, formatNumber } from "@/pages/admin/format";
import { exportToCsv, exportToPdf } from "@/pages/admin/exportUtils";
import {
  buildCouponPerformance,
  buildMarketingChannels,
  groupBookingsByType,
  type ChannelRow,
  type CouponPerfRow,
} from "@/lib/adminReports";

export default function AdminMarketingReports() {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [bookingRows, couponRows, reviewRows] = await Promise.all([
        fetchBookings(),
        listRows<Coupon>("coupons", "used_count"),
        listRows<Review>("reviews", "created_at"),
      ]);
      setBookings(bookingRows);
      setCoupons(couponRows);
      setReviews(reviewRows);
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : "We couldn't load marketing data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const channels = useMemo(() => buildMarketingChannels(bookings), [bookings]);
  const couponRows = useMemo(() => buildCouponPerformance(coupons), [coupons]);
  const typeMix = useMemo(() => groupBookingsByType(bookings), [bookings]);

  const totalGuests = useMemo(() => bookings.reduce((sum, row) => sum + Number(row.group_size || 0), 0), [bookings]);
  const activeCoupons = coupons.filter((coupon) => coupon.status === "active").length;
  const totalRedemptions = coupons.reduce((sum, coupon) => sum + Number(coupon.used_count || 0), 0);
  const avgRating = reviews.length
    ? (reviews.reduce((sum, row) => sum + Number(row.rating || 0), 0) / reviews.length).toFixed(1)
    : "0.0";
  const topChannel = channels[0]?.label || "—";

  const channelColumns: Column<ChannelRow>[] = [
    {
      key: "channel",
      header: "Channel / source",
      primary: true,
      render: (row) => (
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-full bg-secondary-100 text-secondary-700 flex items-center justify-center shrink-0">
            <i className="ri-share-forward-line"></i>
          </span>
          <span className="text-sm font-semibold text-foreground-950 capitalize whitespace-nowrap">{row.label}</span>
        </div>
      ),
    },
    { key: "bookings", header: "Bookings", render: (row) => <span className="text-sm text-foreground-700">{formatNumber(row.bookings)}</span> },
    { key: "guests", header: "Guests", render: (row) => <span className="text-sm text-foreground-700">{formatNumber(row.guests)}</span> },
    {
      key: "share",
      header: "Share",
      render: (row) => (
        <div className="flex items-center gap-2 min-w-[120px]">
          <span className="h-2 w-20 rounded-full bg-background-200 overflow-hidden">
            <span className="block h-full rounded-full bg-primary-500" style={{ width: `${row.share}%` }}></span>
          </span>
          <span className="text-xs text-foreground-600 whitespace-nowrap">{row.share}%</span>
        </div>
      ),
    },
  ];

  const couponColumns: Column<CouponPerfRow>[] = [
    { key: "code", header: "Coupon", primary: true, render: (row) => <span className="text-sm font-semibold text-foreground-950 whitespace-nowrap">{row.code}</span> },
    { key: "discount", header: "Discount", render: (row) => <span className="text-sm text-foreground-700 whitespace-nowrap">{row.discount}</span> },
    { key: "used", header: "Redemptions", render: (row) => <span className="text-sm text-foreground-700 whitespace-nowrap">{row.used} / {row.limit}</span> },
    {
      key: "redemption",
      header: "Redemption rate",
      render: (row) => (
        <div className="flex items-center gap-2 min-w-[120px]">
          <span className="h-2 w-20 rounded-full bg-background-200 overflow-hidden">
            <span className="block h-full rounded-full bg-accent-500" style={{ width: `${Math.min(100, row.redemption)}%` }}></span>
          </span>
          <span className="text-xs text-foreground-600 whitespace-nowrap">{row.redemption}%</span>
        </div>
      ),
    },
    { key: "status", header: "Status", render: (row) => <StatusPill {...commerceMeta(COUPON_STATUS, row.status)} /> },
  ];

  const channelExport = [
    { header: "Channel", value: (row: ChannelRow) => row.label },
    { header: "Bookings", value: (row: ChannelRow) => `${row.bookings}` },
    { header: "Guests", value: (row: ChannelRow) => `${row.guests}` },
    { header: "Share %", value: (row: ChannelRow) => `${row.share}` },
  ];

  const couponExport = [
    { header: "Coupon", value: (row: CouponPerfRow) => row.code },
    { header: "Discount", value: (row: CouponPerfRow) => row.discount },
    { header: "Redemptions", value: (row: CouponPerfRow) => `${row.used}` },
    { header: "Usage limit", value: (row: CouponPerfRow) => `${row.limit}` },
    { header: "Redemption %", value: (row: CouponPerfRow) => `${row.redemption}` },
    { header: "Status", value: (row: CouponPerfRow) => row.status },
  ];

  return (
    <div>
      <PageHeader
        eyebrow="Reports"
        title="Marketing Reports"
        subtitle="Where bookings come from, which coupon campaigns land, and how the business is being talked about — all exportable."
        actions={
          <>
            <button type="button" className={btnGhost} onClick={() => exportToCsv("rythugaru-marketing-channels", channelExport, channels)}>
              <i className="ri-file-excel-2-line"></i> CSV
            </button>
            <button type="button" className={btnPrimary} onClick={() => exportToPdf({ title: "Marketing Report", subtitle: `Channel mix across ${channels.length} sources`, columns: channelExport, rows: channels })}>
              <i className="ri-file-pdf-2-line"></i> PDF
            </button>
          </>
        }
      />

      <StatCards
        stats={[
          { label: "Total bookings", value: formatNumber(bookings.length), icon: "ri-calendar-check-line", tone: "primary" },
          { label: "Total guests", value: formatNumber(totalGuests), icon: "ri-group-line", tone: "primary" },
          { label: "Active coupons", value: activeCoupons, icon: "ri-coupon-3-line", tone: "accent" },
          { label: "Avg. rating", value: avgRating, icon: "ri-star-line", tone: "secondary" },
        ]}
      />

      {error ? (
        <div className="mt-5">
          <ErrorBanner message={error} onRetry={load} onDismiss={() => setError("")} />
        </div>
      ) : null}

      {loading ? (
        <div className="mt-5">
          <LoadingState label="Building marketing report…" />
        </div>
      ) : (
        <div className="mt-5 space-y-6">
          <section>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
              <div>
                <h2 className="font-heading text-xl font-semibold text-foreground-950">Acquisition channels</h2>
                <p className="text-sm text-foreground-600 mt-0.5">Top source: <span className="font-semibold capitalize">{topChannel}</span></p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {typeMix.slice(0, 3).map((item) => (
                  <span key={item.label} className="text-xs font-semibold text-secondary-700 bg-secondary-100 border border-secondary-200 px-2.5 py-1 rounded-full capitalize whitespace-nowrap">
                    {item.label} · {item.value}
                  </span>
                ))}
              </div>
            </div>
            <DataTable columns={channelColumns} rows={channels} />
          </section>

          <section>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
              <div>
                <h2 className="font-heading text-xl font-semibold text-foreground-950">Coupon campaign performance</h2>
                <p className="text-sm text-foreground-600 mt-0.5">{totalRedemptions} redemptions across {couponRows.length} coupons</p>
              </div>
              <div className="flex items-center gap-2">
                <button type="button" className={btnGhost} onClick={() => exportToCsv("rythugaru-coupon-performance", couponExport, couponRows)}>
                  <i className="ri-file-excel-2-line"></i> CSV
                </button>
                <button type="button" className={btnGhost} onClick={() => exportToPdf({ title: "Coupon Campaign Performance", subtitle: `${couponRows.length} coupons`, columns: couponExport, rows: couponRows })}>
                  <i className="ri-file-pdf-2-line"></i> PDF
                </button>
              </div>
            </div>
            <DataTable columns={couponColumns} rows={couponRows} />
          </section>
        </div>
      )}
    </div>
  );
}